import { Autocomplete, TextField, Typography } from '@mui/material';
import theme from 'App.theme';
import { useLanguage } from 'context/AppContext';
import { useIsHospitalViewer } from 'hooks/useIsHospitalViewer';
import { Delivery } from 'api/models/Deliveries';
import Button from 'components/Button';
import ShipperSelect from 'components/ShipperSelect';
import { ReceiveDeliveryFields, useReceiveDeliveryFields } from '../hooks/useReceiveDeliveryFields';
import IncrementDecrement from 'components/IncrementDecrement/IncrementDecrement';
import { useDeliveryContentsOptions } from 'hooks/useDeliveryContentsOptions';
import { OrderSummary } from 'api/models/Orders';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { SelectFieldOption } from 'components/DropDown/ValidatingSelect';
import useValidateOrderNumber from 'api/hooks/useValidateOrderNumber';
import useValidateSurgeryReference from 'api/hooks/useValidateSurgeryReference';
import useFormHelper from 'hooks/useFormHelper';

type Props = {
  delivery?: Delivery;
  matchingOrders?: OrderSummary[];
  supplierRepOptions: SelectFieldOption[];
  onContinue: () => void;
  isSaving: boolean;
};

const DeliverySave = ({ delivery, matchingOrders, supplierRepOptions, onContinue, isSaving }: Props) => {
  const lang = useLanguage()[0].ReceiveDeliveryDialog;

  const { fields, setField } = useReceiveDeliveryFields();
  const { surgeryReferenceValid, validateSurgeryReferenceDebounced } =
    useValidateSurgeryReference<ReceiveDeliveryFields>(fields);
  const { orderNumberValid, isValidatingOrderNumber, validateOrderNumberDebounced } =
    useValidateOrderNumber<ReceiveDeliveryFields>(fields);

  const { isHospitalViewer } = useIsHospitalViewer();
  const disableEdit = isHospitalViewer || Boolean(delivery);

  // surgeryReference and orderNumber are not editable if already set
  const [disableSurgeryReferenceEdit, disableOrderNumberEdit] = useMemo(
    () => [
      (isHospitalViewer && !!delivery?.surgery.surgeryReference) || !!fields.surgeryReference,
      disableEdit || !!fields.orderNumber,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delivery?.surgery.surgeryReference],
  );

  const deliveryContentsOptions = useDeliveryContentsOptions();

  const { order, isSupplierCreated } = useMemo(() => {
    const order = matchingOrders?.find(o => o.orderNumber === fields.orderNumber);
    return { order, isSupplierCreated: !!(order?.supplierCreated || delivery?.supplierSideCreated) };
  }, [delivery?.supplierSideCreated, fields.orderNumber, matchingOrders]);

  // if the order was supplier created the delivery number options are known
  const deliveryNumberOptions = useMemo<string[]>(() => {
    if (!order || !isSupplierCreated) return [];

    const deliveryNumbers = order.deliveries.reduce<string[]>((acc, delivery) => {
      if (delivery.deliveryStage == 'OUTBOUND' || delivery.deliveryNumber == fields.deliveryNumber)
        acc.push(delivery.deliveryNumber);
      return acc;
    }, []);

    // commented out for now as we are allowing bypass of connected suppliers
    /*
    if (!deliveryNumbers.length) {
      throw new Error(
        'Data Error: No receivable deliveries found for supplier created order: ' + order.orderNumber,
      );
    }
    */

    return deliveryNumbers;
  }, [fields.deliveryNumber, isSupplierCreated, order]);

  const textFieldChangeCallback = useCallback(
    (field: keyof ReceiveDeliveryFields, val: string) => {
      if (field == 'surgeryReference') {
        validateSurgeryReferenceDebounced(val);
      } else if (field == 'orderNumber') {
        validateOrderNumberDebounced(val);
      }
    },
    [validateOrderNumberDebounced, validateSurgeryReferenceDebounced],
  );

  useEffect(() => {
    if (!orderNumberValid) toast.error(lang.existingOrderNumber);
  }, [lang.existingOrderNumber, orderNumberValid]);

  const { onTextFieldChange } = useFormHelper<ReceiveDeliveryFields>({
    setField,
    textFieldChangeCallback,
  });

  const disableDeliveryContentsEdit = isHospitalViewer || (isSupplierCreated && !!delivery?.content);

  const hasShipperDetails =
    fields.numRoadCases || fields.numTubs || fields.numBoxes || fields.numPelicanCases || fields.numDoubleRoadCases;

  return (
    <div
      style={{
        marginTop: theme.layout.spacing.tiny,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.layout.spacing.medium,
      }}
    >
      <Typography variant="caption" color="error">
        {lang.helperText}
      </Typography>

      <TextField
        label={lang.formLabels.surgeryReference}
        value={fields.surgeryReference}
        required
        disabled={disableSurgeryReferenceEdit}
        size="small"
        fullWidth
        onKeyUp={event => onTextFieldChange('surgeryReference', event as any)}
        onChange={event => onTextFieldChange('surgeryReference', event as any)}
        error={!surgeryReferenceValid || (!disableSurgeryReferenceEdit && !fields.surgeryReference)}
        helperText={surgeryReferenceValid ? '' : lang.existingSurgeryId}
      />

      {!disableOrderNumberEdit && (
        <TextField
          label={lang.formLabels.orderNumber}
          value={fields.orderNumber}
          size="small"
          fullWidth
          onKeyUp={event => onTextFieldChange('orderNumber', event as any)}
          onChange={event => onTextFieldChange('orderNumber', event as any)}
          error={!orderNumberValid}
          helperText={orderNumberValid ? '' : lang.existingOrderNumber}
          // the dialog is auto-width, so the message must not widen it - wrap it at the field width instead
          FormHelperTextProps={{ sx: { width: 0, minWidth: '100%' } }}
        />
      )}

      {isSupplierCreated &&
        (deliveryNumberOptions.length ? (
          <Autocomplete
            value={fields.deliveryNumber || null}
            options={deliveryNumberOptions}
            disabled={disableEdit}
            size="small"
            renderInput={params => (
              <TextField
                {...params}
                name="deliveryNumber"
                label={lang.formLabels.deliveryNumber}
                required
                error={!disableEdit && !fields.deliveryNumber}
              />
            )}
            onChange={(_, val) => {
              setField('deliveryNumber', val || undefined);
            }}
          />
        ) : (
          <TextField
            label={lang.formLabels.deliveryNumber}
            value={fields.deliveryNumber}
            disabled={disableEdit}
            size="small"
            fullWidth
            onKeyUp={event => onTextFieldChange('deliveryNumber', event as any)}
            onChange={event => onTextFieldChange('deliveryNumber', event as any)}
          />
        ))}

      <Autocomplete
        value={fields.deliveryContents || null}
        options={deliveryContentsOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={disableDeliveryContentsEdit}
        size="small"
        renderInput={params => (
          <TextField
            {...params}
            name="deliveryContents"
            label={lang.formLabels.deliveryContents}
            required
            error={!disableDeliveryContentsEdit && !fields.deliveryContents}
          />
        )}
        onChange={(_, val) => {
          setField('deliveryContents', val || undefined);
        }}
      />

      {supplierRepOptions.length > 0 && (
        <Autocomplete
          value={fields.supplierRep || null}
          options={supplierRepOptions}
          isOptionEqualToValue={(option, value) => option.id == value.id}
          disabled={isHospitalViewer || !!delivery?.supplier.deviceRepId}
          size="small"
          renderInput={params => <TextField {...params} name="supplierRep" label={lang.formLabels.supplierRep} />}
          onChange={(_, val) => {
            setField('supplierRep', val || undefined);
          }}
        />
      )}

      <fieldset
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.layout.spacing.small,
          border: `1px solid ${theme.greys.GREY1}`,
          borderRadius: '4px',
          margin: 0,
          padding: 'calc(0.35em + 8px) 0.75em 0.625em',
        }}
      >
        <legend style={{ color: theme.greys.GREY4 }}>{lang.formLabels.shipmentDetails} *</legend>

        <ShipperSelect
          value={fields.shipper}
          disabled={isHospitalViewer || !!delivery?.shipper}
          required
          onChange={val => setField('shipper', val)}
        />

        <TextField
          label="Shipment ID"
          value={fields.shipmentId || ''}
          disabled={isHospitalViewer || !!delivery?.shipper}
          size="small"
          fullWidth
          onChange={event => setField('shipmentId', event.target.value)}
        />

        <IncrementDecrement
          label={lang.formLabels.numRoadCases}
          count={fields.numRoadCases || 0}
          disabled={isHospitalViewer}
          onChange={val => setField('numRoadCases', val)}
        />

        <IncrementDecrement
          label={lang.formLabels.numDoubleRoadCases}
          count={fields.numDoubleRoadCases || 0}
          disabled={isHospitalViewer}
          onChange={val => setField('numDoubleRoadCases', val)}
        />

        <IncrementDecrement
          label={lang.formLabels.numPelicanCases}
          count={fields.numPelicanCases || 0}
          disabled={isHospitalViewer}
          onChange={val => setField('numPelicanCases', val)}
        />

        <IncrementDecrement
          label={lang.formLabels.numTubs}
          count={fields.numTubs || 0}
          disabled={isHospitalViewer}
          onChange={val => setField('numTubs', val)}
        />

        <IncrementDecrement
          label={lang.formLabels.numBoxes}
          count={fields.numBoxes || 0}
          disabled={isHospitalViewer}
          onChange={val => setField('numBoxes', val)}
        />
      </fieldset>

      <Button
        label={lang.completeReceive}
        colors="white on active"
        onClick={onContinue}
        disabled={
          isHospitalViewer ||
          isSaving ||
          !fields.receivedDate ||
          !fields.supplier ||
          !fields.surgeon ||
          !fields.surgeryDate ||
          !fields.surgeryReference ||
          (deliveryNumberOptions.length && !fields.deliveryNumber) ||
          !surgeryReferenceValid ||
          !orderNumberValid ||
          (!!fields.orderNumber && isValidatingOrderNumber) ||
          !fields.deliveryContents ||
          !fields.shipper ||
          !hasShipperDetails
        }
        loading={isSaving}
        sx={{ height: '40px' }}
      />
    </div>
  );
};

export default DeliverySave;
